/**
 * Insights URL builders
 * @module lib/insights/urls
 */

const { buildMillaAuthUrl } = require('../utils/millaAuth');
const { resolveContestArticleId } = require('../contest/urls');

function resolveInsightsArticleId(explicitId) {
  return resolveContestArticleId(explicitId);
}

function getInsightsPublicOrigin() {
  if (typeof window !== 'undefined') {
    try {
      const config = window.MillaAuth?.getConfig?.() || {};
      if (config.insightsOrigin) return String(config.insightsOrigin).replace(/\/$/, '');
      const issuer = String(config.issuer || '');
      if (/^https?:\/\/(localhost|127\.0\.0\.1)(:|$)/i.test(issuer)) return issuer.replace(/\/$/, '');
      if (/auth-dev/i.test(issuer)) return 'https://insights-dev.narcity.com';
      if (/auth-(sandbox|qa)/i.test(issuer)) return 'https://insights-sandbox.narcity.com';
    } catch (_) {
      // Fall through to the production public origin.
    }
  }
  return 'https://insights.narcity.com';
}

function buildInsightsPublicUrl(path, params = {}) {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  const query = Object.entries(params)
    .filter(([, value]) => value !== undefined && value !== null && value !== '')
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
    .join('&');
  return `${getInsightsPublicOrigin()}${normalizedPath}${query ? `?${query}` : ''}`;
}

function buildInsightsActiveUrl(articleId, options = {}) {
  const resolved = resolveInsightsArticleId(articleId);
  const brand = options.brand === 'mtlblog' ? 'mtlblog' : 'narcity';
  const params = { brand };
  if (resolved) params.articleId = resolved;
  if (Array.isArray(options.tags) && options.tags.length) {
    params.tags = options.tags.join(',');
  }
  if (options.language === 'fr' || options.language === 'en') {
    params.language = options.language;
  }
  if (options.sponsored === true || String(options.sponsored || '').trim().toLowerCase() === 'yes') {
    params.sponsored = 'yes';
  }
  if (Array.isArray(options.excludeSurveyIds) && options.excludeSurveyIds.length) {
    params.exclude = options.excludeSurveyIds.join(',');
  }
  // A brand lift study survey is served only to a reader who names its study
  // and a group (lib/insights/study.js); neither is sent without a group.
  if (options.study && (options.group === 'exposed' || options.group === 'control')) {
    params.study = options.study;
    params.group = options.group;
  }
  return buildMillaAuthUrl('/api/embed/active', params);
}

function buildInsightsIframeUrl(surveyId, options = {}) {
  const id = typeof surveyId === 'string' ? surveyId.trim() : '';
  if (!id) return '';

  const articleId = resolveInsightsArticleId(options.articleId);
  const location = typeof options.location === 'string' && options.location.trim()
    ? options.location.trim()
    : 'article_end';
  const sourceUrl = typeof window !== 'undefined' && window.location
    ? window.location.href
    : null;
  const language = options.language === 'fr' ? 'fr' : 'en';
  const brand = options.brand === 'mtlblog' ? 'mtlblog' : 'narcity';

  const params = {
    surveyId: id,
    location,
    language,
    brand
  };
  if (articleId) params.articleId = articleId;
  if (sourceUrl) params.sourceUrl = sourceUrl;
  if (options.impressionId) params.impressionId = options.impressionId;

  return buildMillaAuthUrl('/embed/insights', params);
}

function buildPredictionIframeUrl(publicSlug, options = {}) {
  const slug = typeof publicSlug === 'string' ? publicSlug.trim().toLowerCase() : '';
  if (!slug) return '';
  return buildInsightsPublicUrl(`/_embed/predictions/${encodeURIComponent(slug)}`, {
    articleId: resolveInsightsArticleId(options.articleId) || undefined,
    language: options.language === 'fr' ? 'fr' : 'en',
    brand: options.brand === 'mtlblog' ? 'mtlblog' : 'narcity'
  });
}

module.exports = {
  resolveInsightsArticleId,
  getInsightsPublicOrigin,
  buildInsightsPublicUrl,
  buildInsightsActiveUrl,
  buildInsightsIframeUrl,
  buildPredictionIframeUrl
};
